import { ComponentProps, ReactNode } from "react";
import { useDropzone as useReactDropzone } from "react-dropzone";
import {
  Controller,
  FieldPath,
  FieldPathByValue,
  FieldValues,
  UseControllerProps,
  useFormContext,
  UseFormReturn,
} from "react-hook-form";
import { MIME_TYPE } from "common/constants";
import { useSingleFileUpload } from "../../hooks/useSingleFileUpload";
import {
  getAllowedExtensions,
  mergeFileValidationRules,
  validFileExtension,
} from "../../utils/fileUploadValidation";
import {
  DropzoneComponent,
  Filename,
  FileRowComponent,
  FileTable,
  Info,
  Remove,
  SuccessOrErrorIcon,
} from "../FileUploads";
import Label from "./Label";

export type Props<
  TFieldValues extends FieldValues,
  Path extends FieldPath<TFieldValues>,
> = Omit<UseControllerProps<TFieldValues, Path>, "control"> & {
  // we specifically require a `control` prop to allow
  // TS to infer the possible types of `name` and `rules`
  control: UseControllerProps<TFieldValues, Path>["control"];
  allowedMimeTypes: Array<MIME_TYPE>;
  fileHeader?: ReactNode;
  useDropzone?: (
    args: Pick<NonNullable<Parameters<typeof useReactDropzone>[0]>, "onDrop">,
  ) => ReturnType<typeof useReactDropzone>;
  label?: string;
  required?: boolean;
  description?: string;
  formMethods?: Pick<
    UseFormReturn<TFieldValues>,
    "setValue" | "watch" | "resetField" | "control"
  >;
} & Pick<ComponentProps<typeof DropzoneComponent>, "compact" | "hasImage">;

export const ReactHookFormSingleFileUpload = <
  Form extends FieldValues,
  Path extends FieldPathByValue<Form, { blob: any }>,
>({
  useDropzone = undefined,
  allowedMimeTypes,
  name: fieldName,
  rules,
  fileHeader,
  control,
  compact,
  hasImage,
  label,
  required,
  description,
  formMethods: providedFormMethods,
}: Props<Form, Path>) => {
  const formContext = useFormContext<Form>();
  const formMethods =
    providedFormMethods ||
    (formContext
      ? {
          setValue: formContext.setValue,
          watch: formContext.watch,
          resetField: formContext.resetField,
          control: formContext.control,
        }
      : undefined);

  const { currentValue, onDrop, resetField } = useSingleFileUpload<Form>(
    fieldName,
    formMethods,
  );

  const allowedExtensions = getAllowedExtensions(allowedMimeTypes);

  const DropzoneContent = () => (
    <>
      <h5>Drop your file here</h5>
      <p>
        or <span>browse</span> to choose a file
      </p>
    </>
  );

  const validate = mergeFileValidationRules<Form[Path]>(rules, {
    "file-extension": (file: Form[Path]) =>
      validFileExtension(
        file as { originalFilename: string; blob: unknown },
        allowedExtensions,
      ),
  });

  return (
    <>
      {!!label && (
        <Label text={label!} required={required} description={description} />
      )}
      <Controller
        control={control}
        name={fieldName}
        rules={{ validate, ...rules }}
        render={({ fieldState }) => {
          return !currentValue?.blob ? (
            <DropzoneComponent
              onDrop={onDrop}
              allowedMimeTypes={allowedMimeTypes}
              content={DropzoneContent}
              useDropzone={useDropzone}
              hasImage={hasImage}
              compact={compact}
            />
          ) : (
            <FileTable>
              {fileHeader}
              <FileRowComponent isErrorRow={!fieldState.error} compact={true}>
                <Info>
                  <SuccessOrErrorIcon success={!fieldState.error} />
                  <Filename
                    filename={currentValue.originalFilename}
                    errorMessage={fieldState.error?.message}
                  />
                </Info>
                <Remove onClick={resetField} />
              </FileRowComponent>
            </FileTable>
          );
        }}
      />
    </>
  );
};
